import Phaser from 'phaser';
import { NetworkManager } from '../managers/NetworkManager';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  GameOverMessage,
  ClientGameState,
  PlayerPublicState,
  PlayerPosition,
} from '@hokm/shared';
import { HelpSidebar } from '../objects/HelpSidebar';
import { PERSIAN_THEME } from '../theme/persianTheme';
import { applyRenderScale } from '../config';
import { UrlStateManager } from '../utils/UrlStateManager';
import { SceneTransition } from '../utils/SceneTransition';
import { ConfettiEmitter, ConfettiColorKey } from '../objects/ConfettiEmitter';

export class GameOverScene extends Phaser.Scene {
  private networkManager!: NetworkManager;
  private confettiEmitter: ConfettiEmitter | null = null;

  constructor() {
    super({ key: 'GameOverScene' });
  }

  create(): void {
    applyRenderScale(this);

    // Card fly-in entrance animation
    SceneTransition.cardFlyIn(this);

    this.networkManager = this.registry.get('networkManager');
    const finalScore = this.registry.get('finalScore') as GameOverMessage | null;
    const gameState = this.registry.get('gameState') as ClientGameState | null;

    // Determine winner info and colors (needed for background)
    const winnerInfo = this.getWinnerInfo(finalScore, gameState);

    // Create layered background
    this.createBackground(winnerInfo.color);

    // Start confetti burst after a short delay
    this.time.delayedCall(200, () => {
      this.confettiEmitter = new ConfettiEmitter(this, winnerInfo.colorKey);
      this.confettiEmitter.burst();
    });

    // Create UI elements with staggered animations
    this.createVictoryTitle(400);
    this.createWinnerAnnouncement(winnerInfo, 600);
    this.createScoreBox(finalScore, gameState, winnerInfo, 800);
    this.createButtons(1000);

    // Help sidebar (self-managing component)
    new HelpSidebar(this);
  }

  /**
   * Create layered background with dark green base, vignette, and winner color glow
   */
  private createBackground(winnerColor: number): void {
    const centerX = GAME_WIDTH / 2;
    const centerY = GAME_HEIGHT / 2;

    // Dark green base
    this.add.rectangle(centerX, centerY, GAME_WIDTH, GAME_HEIGHT, PERSIAN_THEME.colors.darkOverlay);

    // Winner color radial glow from center
    const glowGraphics = this.add.graphics();

    // Inner bright glow
    const glowSteps = 12;
    for (let i = glowSteps; i >= 0; i--) {
      const radius = 50 + i * 40;
      const alpha = 0.08 * (1 - i / glowSteps);
      glowGraphics.fillStyle(winnerColor, alpha);
      glowGraphics.fillCircle(centerX, centerY - 50, radius);
    }

    // Vignette effect (darker edges)
    const vignetteGraphics = this.add.graphics();
    const vignetteSteps = 10;
    for (let i = 0; i < vignetteSteps; i++) {
      const alpha = 0.12 * (i / vignetteSteps);
      const inset = i * 40;
      vignetteGraphics.fillStyle(0x000000, alpha);
      // Top edge
      vignetteGraphics.fillRect(0, 0, GAME_WIDTH, inset);
      // Bottom edge
      vignetteGraphics.fillRect(0, GAME_HEIGHT - inset, GAME_WIDTH, inset);
      // Left edge
      vignetteGraphics.fillRect(0, 0, inset, GAME_HEIGHT);
      // Right edge
      vignetteGraphics.fillRect(GAME_WIDTH - inset, 0, inset, GAME_HEIGHT);
    }

    // Subtle decorative corner accents
    this.createCornerAccents(winnerColor);
  }

  /**
   * Create subtle decorative accents in corners
   */
  private createCornerAccents(color: number): void {
    const graphics = this.add.graphics();
    graphics.lineStyle(2, color, 0.3);

    const cornerSize = 60;
    const margin = 30;

    // Top-left corner
    graphics.beginPath();
    graphics.moveTo(margin, margin + cornerSize);
    graphics.lineTo(margin, margin);
    graphics.lineTo(margin + cornerSize, margin);
    graphics.strokePath();

    // Top-right corner
    graphics.beginPath();
    graphics.moveTo(GAME_WIDTH - margin - cornerSize, margin);
    graphics.lineTo(GAME_WIDTH - margin, margin);
    graphics.lineTo(GAME_WIDTH - margin, margin + cornerSize);
    graphics.strokePath();

    // Bottom-left corner
    graphics.beginPath();
    graphics.moveTo(margin, GAME_HEIGHT - margin - cornerSize);
    graphics.lineTo(margin, GAME_HEIGHT - margin);
    graphics.lineTo(margin + cornerSize, GAME_HEIGHT - margin);
    graphics.strokePath();

    // Bottom-right corner
    graphics.beginPath();
    graphics.moveTo(GAME_WIDTH - margin - cornerSize, GAME_HEIGHT - margin);
    graphics.lineTo(GAME_WIDTH - margin, GAME_HEIGHT - margin);
    graphics.lineTo(GAME_WIDTH - margin, GAME_HEIGHT - margin - cornerSize);
    graphics.strokePath();
  }

  /**
   * Extract winner information based on game mode
   */
  private getWinnerInfo(
    finalScore: GameOverMessage | null,
    gameState: ClientGameState | null
  ): {
    text: string;
    colorKey: ConfettiColorKey;
    color: number;
    hexColor: string;
  } {
    if (!finalScore) {
      return {
        text: 'Victory!',
        colorKey: 'teamA',
        color: PERSIAN_THEME.colors.teamA,
        hexColor: PERSIAN_THEME.hex.teamA,
      };
    }

    if (finalScore.mode === 'team') {
      const isTeamA = finalScore.winningTeam === 'A';
      const teamName = PERSIAN_THEME.teamNames[finalScore.winningTeam];
      return {
        text: `${teamName} Team Wins!`,
        colorKey: isTeamA ? 'teamA' : 'teamB',
        color: isTeamA ? PERSIAN_THEME.colors.teamA : PERSIAN_THEME.colors.teamB,
        hexColor: isTeamA ? PERSIAN_THEME.hex.teamA : PERSIAN_THEME.hex.teamB,
      };
    } else {
      // Individual mode
      const winner = gameState?.players?.find((p: PlayerPublicState) => p?.id === finalScore.winnerId);
      const winnerName = winner?.name || 'Player';

      // Determine player color based on position
      const position = winner?.position;

      // Player 2 is WEST/NORTH, Player 3 is EAST, Player 1 is SOUTH (default)
      if (position === PlayerPosition.WEST || position === PlayerPosition.NORTH) {
        return {
          text: `${winnerName} Wins!`,
          colorKey: 'player2' as ConfettiColorKey,
          color: PERSIAN_THEME.colors.player2,
          hexColor: PERSIAN_THEME.hex.player2,
        };
      } else if (position === PlayerPosition.EAST) {
        return {
          text: `${winnerName} Wins!`,
          colorKey: 'player3' as ConfettiColorKey,
          color: PERSIAN_THEME.colors.player3,
          hexColor: PERSIAN_THEME.hex.player3,
        };
      }

      // Default: SOUTH or unknown position
      return {
        text: `${winnerName} Wins!`,
        colorKey: 'player1' as ConfettiColorKey,
        color: PERSIAN_THEME.colors.player1,
        hexColor: PERSIAN_THEME.hex.player1,
      };
    }
  }

  /**
   * Create the "VICTORY" title with animation
   */
  private createVictoryTitle(delay: number): void {
    const title = this.add
      .text(GAME_WIDTH / 2, 120, 'VICTORY', {
        fontFamily: 'Georgia, serif',
        fontSize: '72px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setScale(0.5);

    // Fade in and scale bounce
    this.time.delayedCall(delay, () => {
      this.tweens.add({
        targets: title,
        alpha: 1,
        scale: 1,
        duration: 400,
        ease: 'Back.easeOut',
      });
    });
  }

  /**
   * Create the winner announcement text
   */
  private createWinnerAnnouncement(winnerInfo: { text: string; hexColor: string }, delay: number): void {
    const announcement = this.add
      .text(GAME_WIDTH / 2, 210, winnerInfo.text, {
        fontFamily: 'Georgia, serif',
        fontSize: '48px',
        color: winnerInfo.hexColor,
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setAlpha(0);

    // Slide in from below
    announcement.y += 30;
    this.time.delayedCall(delay, () => {
      this.tweens.add({
        targets: announcement,
        alpha: 1,
        y: announcement.y - 30,
        duration: 400,
        ease: 'Power2.easeOut',
      });
    });
  }

  /**
   * Create the styled score box
   */
  private createScoreBox(
    finalScore: GameOverMessage | null,
    gameState: ClientGameState | null,
    winnerInfo: { color: number },
    delay: number
  ): void {
    if (!finalScore) return;

    const boxWidth = 320;
    const numPlayers = finalScore.mode === 'team' ? 0 : Object.keys(finalScore.finalScore).length;
    const boxHeight = finalScore.mode === 'team' ? 100 : Math.min(70 + numPlayers * 28, 220);
    const boxX = GAME_WIDTH / 2;
    const boxY = 340;

    // Container for box
    const container = this.add.container(boxX, boxY).setAlpha(0);

    // Box background with border
    const bg = this.add.rectangle(0, 0, boxWidth, boxHeight, 0x1a2a3a, 0.9).setStrokeStyle(3, winnerInfo.color);
    container.add(bg);

    // "Final Score" header
    const header = this.add
      .text(0, -boxHeight / 2 + 20, 'Final Score', {
        fontFamily: 'Arial',
        fontSize: '18px',
        color: PERSIAN_THEME.hex.textSecondary,
      })
      .setOrigin(0.5);
    container.add(header);

    if (finalScore.mode === 'team') {
      // Team mode: side by side scores
      const scoreA = finalScore.finalScore.teamA;
      const scoreB = finalScore.finalScore.teamB;

      // Team A (Gold)
      const teamALabel = this.add
        .text(-70, 10, PERSIAN_THEME.teamNames.A, {
          fontFamily: 'Arial',
          fontSize: '16px',
          color: PERSIAN_THEME.hex.teamA,
        })
        .setOrigin(0.5);

      const teamAScore = this.add
        .text(-70, 35, String(scoreA), {
          fontFamily: 'Georgia, serif',
          fontSize: '32px',
          color: PERSIAN_THEME.hex.teamA,
        })
        .setOrigin(0.5);

      // Divider
      const divider = this.add
        .text(0, 25, '-', {
          fontFamily: 'Arial',
          fontSize: '28px',
          color: '#ffffff',
        })
        .setOrigin(0.5);

      // Team B (Teal)
      const teamBLabel = this.add
        .text(70, 10, PERSIAN_THEME.teamNames.B, {
          fontFamily: 'Arial',
          fontSize: '16px',
          color: PERSIAN_THEME.hex.teamB,
        })
        .setOrigin(0.5);

      const teamBScore = this.add
        .text(70, 35, String(scoreB), {
          fontFamily: 'Georgia, serif',
          fontSize: '32px',
          color: PERSIAN_THEME.hex.teamB,
        })
        .setOrigin(0.5);

      container.add([teamALabel, teamAScore, divider, teamBLabel, teamBScore]);
    } else {
      // Individual mode: list all players
      const scores = finalScore.finalScore as { [key: string]: number };
      const sortedScores = Object.entries(scores).sort((a, b) => b[1] - a[1]);

      let yOffset = 5;
      for (const [playerId, pts] of sortedScores) {
        const player = gameState?.players?.find((p: PlayerPublicState) => p?.id === playerId);
        const name = player?.name || 'Player';
        const scoreText = this.add
          .text(0, yOffset, `${name}: ${pts}`, {
            fontFamily: 'Arial',
            fontSize: '20px',
            color: '#ffffff',
          })
          .setOrigin(0.5);
        container.add(scoreText);
        yOffset += 28;
      }
    }

    // Fade in animation
    this.time.delayedCall(delay, () => {
      this.tweens.add({
        targets: container,
        alpha: 1,
        duration: 400,
        ease: 'Power2.easeOut',
      });
    });
  }

  /**
   * Create Play Again and Leave buttons
   */
  private createButtons(delay: number): void {
    // Play Again - large, prominent button with pulse
    const playAgainBtn = this.createPlayAgainButton(GAME_WIDTH / 2, 480);
    playAgainBtn.setAlpha(0);

    // Leave - subtle text link
    const leaveBtn = this.createLeaveButton(GAME_WIDTH / 2, 550);
    leaveBtn.setAlpha(0);

    // Fade in buttons
    this.time.delayedCall(delay, () => {
      this.tweens.add({
        targets: [playAgainBtn, leaveBtn],
        alpha: 1,
        duration: 400,
        ease: 'Power2.easeOut',
        onComplete: () => {
          // Start pulse animation on Play Again
          this.tweens.add({
            targets: playAgainBtn,
            scaleX: 1.05,
            scaleY: 1.05,
            duration: 800,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
          });
        },
      });
    });
  }

  /**
   * Create the large Play Again button
   */
  private createPlayAgainButton(x: number, y: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const width = 260;
    const height = 56;

    const bg = this.add
      .rectangle(0, 0, width, height, PERSIAN_THEME.colors.buttonBg)
      .setStrokeStyle(2, PERSIAN_THEME.colors.gold);

    const label = this.add
      .text(0, 0, 'PLAY AGAIN', {
        fontFamily: 'Georgia, serif',
        fontSize: '24px',
        color: PERSIAN_THEME.hex.textPrimary,
      })
      .setOrigin(0.5);

    container.add([bg, label]);
    container.setSize(width, height);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerover', () => {
      bg.setFillStyle(PERSIAN_THEME.colors.buttonHover);
      bg.setStrokeStyle(2, PERSIAN_THEME.colors.textHighlight);
    });

    container.on('pointerout', () => {
      bg.setFillStyle(PERSIAN_THEME.colors.buttonBg);
      bg.setStrokeStyle(2, PERSIAN_THEME.colors.gold);
    });

    container.on('pointerdown', () => this.handlePlayAgain());

    return container;
  }

  /**
   * Create the subtle Leave button
   */
  private createLeaveButton(x: number, y: number): Phaser.GameObjects.Text {
    const label = this.add
      .text(x, y, 'Leave', {
        fontFamily: 'Arial',
        fontSize: '18px',
        color: PERSIAN_THEME.hex.textSecondary,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    label.on('pointerover', () => {
      label.setColor('#ffffff');
    });

    label.on('pointerout', () => {
      label.setColor(PERSIAN_THEME.hex.textSecondary);
    });

    label.on('pointerdown', () => this.handleLeave());

    return label;
  }

  /**
   * Handle Play Again button click
   */
  private handlePlayAgain(): void {
    this.cleanup();
    this.networkManager.disconnect();
    UrlStateManager.clearRoomParams();
    UrlStateManager.clearSession();
    // Card fly-out when leaving game (per #18)
    SceneTransition.cardFlyOut(this, 'LobbyScene', { fromGame: true });
  }

  /**
   * Handle Leave button click
   */
  private handleLeave(): void {
    this.cleanup();
    this.networkManager.disconnect();
    UrlStateManager.clearRoomParams();
    UrlStateManager.clearSession();
    // Card fly-out when leaving game (per #18)
    SceneTransition.cardFlyOut(this, 'LobbyScene', { fromGame: true });
  }

  /**
   * Clean up resources
   */
  private cleanup(): void {
    if (this.confettiEmitter) {
      this.confettiEmitter.destroy();
      this.confettiEmitter = null;
    }
  }
}
